"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Renders `placeholder` until the wrapper is close to the viewport, then
 * mounts `children`. Used to defer the map (Leaflet JS + CSS + OSM tiles)
 * until the visitor actually scrolls toward it.
 */
export function LazyMount({
  children,
  placeholder,
  rootMargin = "400px 0px",
  className = "",
}: {
  children: ReactNode;
  placeholder: ReactNode;
  rootMargin?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || show) return;
    if (typeof IntersectionObserver === "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- no observer: mount at once
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, show]);

  return (
    <div ref={ref} className={className}>
      {show ? children : placeholder}
    </div>
  );
}
